import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import UpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import LimpiarFiltros from '../../assets/images/limpiar-filtros.svg';
import { StyledTooltip } from '../../commons/utils/TableStyles.js';
import * as Utils from '../../commons/utils/Utils.js';
import InventarioManoObraGridRow from './InventarioManoObraGridRow.jsx';
import InventarioManoObraGridFiltros from './InventarioManoObraGridFiltros.jsx';
import InventarioManoObraImportModal from './InventarioManoObraImportModal.jsx';
import InventarioManoObraHistorialModal from './InventarioManoObraHistorialModal.jsx';
import GridSearchResultsBadge from '../../commons/filters/GridSearchResultsBadge.jsx';
import GridPagination from '../../commons/grid/GridPagination.jsx';
import SortableCell from '../../commons/grid/SortableCell.jsx';
import { GridToolbar, GridToolbarButton } from '../../commons/grid/GridToolbar.jsx';
import { GridEmptyRow, GridLoadingRow } from '../../commons/grid/GridStateRow.jsx';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

const COLUMN_COUNT = 7;

// El default de `GridPagination` es la constante compartida `rowsPerPage` = [10,25,50,100], que
// NO incluye el 5. Se pasa la lista propia para no sacarle al usuario una opción que esta grilla
// ya ofrecía; alinearse con la constante es una decisión aparte.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

const PAGINATION_INIT = { page: 0, count: 0, perPage: 10 };

const FILTER_QUERY_INIT = {
  numeroDeParte: '',
  descripcion: '',
  categoria: '',
  subcategoria: '',
  showInactivos: false,
};

const SORT_QUERY_INIT = {
  numeroDeParte: '',
  descripcion: 'asc',
  costo: '',
};

const InventarioManoObraGrid = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  const [searchActive, setSearchActive] = useState(false);
  const [pagination, setPagination] = useState({ ...PAGINATION_INIT });
  const [count, setCount] = useState(0);

  const [filterResetInputs, setFilterResetInputs] = useState(false);
  const [filterQuery, setFilterQuery] = useState({ ...FILTER_QUERY_INIT });

  const [sortQuery, setSortQuery] = useState({ ...SORT_QUERY_INIT });
  const [dropdownActive, setDropdownActive] = useState(false);

  const [rows, setRows] = useState([]);

  const [modalImportOpen, setModalImportOpen] = useState(false);
  const [itemHistorial, setItemHistorial] = useState(null);

  const [permissions, setPermissions] = useState({
    crear: false,
    visualizar: false,
    modificar: false,
    desactivar: false,
    importar: false,
  });

  useEffect(() => {
    setLoading(true);
    if (
      Security.hasPermission('INVENTARIO_MANO_OBRA_LISTAR') &&
      ConfigBusiness.get('inventarioItems.habilitado') === 'true'
    ) {
      securityPermission();
      fetchRows();
    } else {
      setRedirectTo('/error');
    }
    return () => {};
  }, [pagination, filterQuery, sortQuery]);

  const securityPermission = () => {
    let aux = { ...permissions };
    aux.crear = Security.hasPermission('INVENTARIO_MANO_OBRA_CREAR');
    aux.visualizar = Security.hasPermission('INVENTARIO_MANO_OBRA_VISUALIZAR');
    aux.modificar = Security.hasPermission('INVENTARIO_MANO_OBRA_MODIFICAR');
    aux.desactivar = Security.hasPermission('INVENTARIO_MANO_OBRA_DESACTIVAR');
    aux.importar = Security.hasPermission('INVENTARIO_MANO_OBRA_IMPORTAR');
    setPermissions(aux);
  };

  const fetchRows = () => {
    Utils.getData(`/inventario/mano-obra/grid${getQuery()}`)
      .then((data) => {
        setRows((data && data.items) || []);
        setCount((data && data.pagination && data.pagination.count) || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getQuery = () => {
    let query = '?page=' + (pagination.page + 1); // +1 porque la API pagina en base 1 y el front en base 0
    query += '&perPage=' + pagination.perPage;
    query += '&numeroDeParte=' + encodeURIComponent(filterQuery.numeroDeParte || '');
    query += '&descripcion=' + encodeURIComponent(filterQuery.descripcion || '');
    query += '&categoria=' + (filterQuery.categoria || '');
    query += '&subcategoria=' + (filterQuery.subcategoria || '');
    query += '&showInactivos=' + (filterQuery.showInactivos ? 'true' : 'false');
    Object.keys(SORT_QUERY_INIT).forEach((key) => {
      if (sortQuery[key]) query += '&' + key + 'Sort=' + sortQuery[key];
    });
    return query;
  };

  const handleChangePage = (event, newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ ...pagination, perPage: parseInt(event.target.value, 10), page: 0 });
  };

  const requestSort = (key) => {
    let query = { ...sortQuery };
    let aux = sortQuery[key];
    Object.keys(SORT_QUERY_INIT).forEach((k) => {
      query[k] = '';
    });
    if (!aux) query[key] = 'asc';
    else if (aux === 'desc') query[key] = 'asc';
    else query[key] = 'desc';
    setPagination({ ...pagination, page: 0 });
    setSortQuery(query);
  };

  // LIMPIAR LA BÚSQUEDA — es lo que hace la "x" de la franja de resultados.
  // Devuelve los filtros al default y oculta la franja. NO toca el ordenamiento: eso
  // es del reset total. Ver docs/design-system/grid-filter-panel.md ("Dos resets, no uno").
  const handleSearchClear = () => {
    setPagination(PAGINATION_INIT);
    setFilterQuery(FILTER_QUERY_INIT);
    setSearchActive(false);
    setFilterResetInputs(true);
  };

  // RESET TOTAL — es lo que hace el botón "Restaurar" del toolbar: además de limpiar la
  // búsqueda, devuelve el ordenamiento por defecto. Reusa el parcial para no duplicar el
  // reset; React batchea los setState del handler, así que sigue habiendo un solo fetch.
  // (Estas grillas no tienen selector de columnas, así que ese tramo del patrón no aplica.)
  const handleFilterReset = () => {
    setSortQuery(SORT_QUERY_INIT);
    setDropdownActive(false);
    handleSearchClear();
  };

  const handleFilterChange = () => {
    setPagination((prevState) => ({ ...prevState, page: 0 }));
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-content collpase show">
              <div className="card-body card-dashboard">
                <div className="container-fluid">
                  <div className="row dt-icons">
                    <div className="col">
                      {permissions.crear && (
                        <StyledTooltip
                          title={intl.formatMessage({ id: 'Nuevo', defaultMessage: 'Nuevo' })}
                          arrow
                          placement="right"
                        >
                          <div
                            className="btn btn-primary box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                            onClick={() => setRedirectTo(props.match.url + '/add')}
                          >
                            <i className="ft-plus"></i>
                          </div>
                        </StyledTooltip>
                      )}
                      <div
                        className="btn btn-grey-blue box-shadow-2 btn-round btn-lg btn-dt-main round-icon"
                        onClick={() => setDropdownActive(!dropdownActive)}
                      >
                        {dropdownActive ? <UpIcon /> : <ExpandMoreIcon />}
                      </div>
                    </div>
                    {/* Franja de resultados: componente compartido src/commons/filters/GridSearchResultsBadge.jsx */}
                    <div className="col">
                      <GridSearchResultsBadge
                        visible={searchActive}
                        mostrados={rows.length}
                        total={count}
                        onReset={() => handleSearchClear()}
                      />
                    </div>
                    {/* Tercera columna del toolbar: las herramientas de la grilla (restaurar e
                        importar), con el contenedor y la forma de cada botón del componente
                        compartido src/commons/grid/GridToolbar.jsx. El de restaurar lleva un `img`
                        en vez de una clase de icono, así que va por `children`. */}
                    <div className="col">
                      <GridToolbar>
                        <GridToolbarButton
                          messageId="inventarioManoObra.grid.tools_restore_filters.title"
                          label="Restaurar filtros y ordenamiento"
                          onClick={() => handleFilterReset()}
                        >
                          <img id="filterReset" className="filter-reset" src={LimpiarFiltros} alt="" />
                        </GridToolbarButton>
                        {permissions.importar && (
                          <GridToolbarButton
                            messageId="inventarioManoObra.import.titulo"
                            label="Importación masiva de mano de obra"
                            onClick={() => setModalImportOpen(true)}
                            icon="ft-download"
                          />
                        )}
                      </GridToolbar>
                    </div>
                  </div>
                </div>

                <InventarioManoObraGridFiltros
                  dropdownActive={dropdownActive}
                  setDropdownActive={setDropdownActive}
                  filterResetInputs={filterResetInputs}
                  setFilterResetInputs={setFilterResetInputs}
                  filterQuery={filterQuery}
                  setFilterQuery={setFilterQuery}
                  searchActive={searchActive}
                  setSearchActive={setSearchActive}
                  pagination={pagination}
                  handleFilterChange={handleFilterChange}
                  loading={loading}
                />

                <TableContainer>
                  <Table aria-label="inventario mano de obra table" className="table-hover">
                    {/* Encabezados: src/commons/grid/SortableCell.jsx. `sort` recibe el objeto con
                        una clave por columna, que es la forma que ya tenía esta grilla y la que
                        viaja al backend. Las columnas sin `onSort` no son ordenables: sin cursor,
                        sin click y sin icono — el endpoint no las soporta. */}
                    <TableHead>
                      <TableRow>
                        <SortableCell
                          field="numeroDeParte"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioManoObra.grid.column_numero_de_parte"
                          label="Nº de parte"
                        />
                        <SortableCell
                          field="descripcion"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioManoObra.grid.column_descripcion"
                          label="Descripción"
                        />
                        <SortableCell
                          field="categoria"
                          messageId="inventarioManoObra.grid.column_categoria"
                          label="Categoría"
                        />
                        <SortableCell
                          field="subcategoria"
                          messageId="inventarioManoObra.grid.column_subcategoria"
                          label="Subcategoría"
                        />
                        <SortableCell
                          field="referencia"
                          messageId="inventarioManoObra.grid.column_referencia"
                          label="Referencia"
                        />
                        <SortableCell
                          field="costo"
                          align="right"
                          sort={sortQuery}
                          onSort={requestSort}
                          messageId="inventarioManoObra.grid.column_costo"
                          label="Costo"
                        />
                        <SortableCell
                          field="acciones"
                          align="center"
                          style={{ width: '170px' }}
                          messageId="inventarioManoObra.grid.column_acciones"
                          label="Acciones"
                        />
                      </TableRow>
                    </TableHead>
                    {/* Filas de estado: src/commons/grid/GridStateRow.jsx. El `colSpan` sale de
                        COLUMN_COUNT, que es la cantidad real de columnas de esta grilla. */}
                    <TableBody>
                      <GridLoadingRow visible={loading} columns={COLUMN_COUNT} />
                      {!loading &&
                        (rows.length > 0 ? (
                          rows.map((row) => (
                            <InventarioManoObraGridRow
                              intl={intl}
                              key={'GridRow_' + row.id}
                              baseUrl={props.match.url}
                              row={row}
                              fetchRows={fetchRows}
                              permissions={permissions}
                              onVerHistorial={setItemHistorial}
                            />
                          ))
                        ) : (
                          <GridEmptyRow
                            columns={COLUMN_COUNT}
                            messageId="inventarioManoObra.grid.no_data_table"
                            label="No se encontraron resultados..."
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Paginación: src/commons/grid/GridPagination.jsx, con los labels i18n. */}
                <GridPagination
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  count={count}
                  page={pagination.page}
                  perPage={pagination.perPage}
                  onPageChange={handleChangePage}
                  onPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <InventarioManoObraImportModal
        open={modalImportOpen}
        onClose={() => setModalImportOpen(false)}
        onImportado={fetchRows}
      />

      <InventarioManoObraHistorialModal item={itemHistorial} onClose={() => setItemHistorial(null)} />
    </>
  );
};

export default InventarioManoObraGrid;
